import { useEffect, useMemo, useRef, useState } from "react";
import { FaSearch, FaTimes } from "react-icons/fa";
import "../styles/search_bar.scss";

// Fields never worth searching or offering as a "generic" option — internal
// bookkeeping, geometry, ids that are already covered by a friendlier field.
const FIELD_BLACKLIST = new Set([
  "geometry", "geom", "uid", "scuid", "cuid", "__gid", "path",
  "featureId", "layerName", "classKey", "gid", "id", "fid",
]);

// Per-layer search config: which fields to match against. "Customer
// Meters" is explicit per the requested column list (account_name,
// account_no, walk_route, serial_no, material); every other layer falls
// back to auto-detecting searchable fields from its own data.
const SEARCH_FIELDS = {
  "Customer Meters": ["accountName", "accountNo", "walkRoute", "meterSerial", "meterMaterial"],
};

// Exported so gis_customers.jsx can compare against it when it needs to
// know whether the search bar's scope is pinned to one specific layer
// (e.g. to decide what the Insights panel should drill into).
export const ALL_LAYERS_KEY = "__all__";

function deriveSearchFields(sample) {
  if (!sample) return [];
  return Object.keys(sample)
    .filter((key) => !FIELD_BLACKLIST.has(key))
    .filter((key) => typeof sample[key] === "string" || typeof sample[key] === "number")
    .slice(0, 6);
}

function getSearchFields(layerName, data) {
  return SEARCH_FIELDS[layerName] || deriveSearchFields(data && data[0]);
}

// `scope`/`setScope` are optionally controlled from the parent (gis_customers.jsx
// lifts them up so the Insights panel can show detail for whatever layer is
// currently being searched). Falls back to local state so this component
// still works standalone if rendered without those props.
export default function SearchBar({ layers, setLayers, scope: scopeProp, setScope: setScopeProp }) {
  const [query, setQuery] = useState("");
  const [internalScope, setInternalScope] = useState(ALL_LAYERS_KEY);
  const scope = scopeProp !== undefined ? scopeProp : internalScope;
  const setScope = setScopeProp || setInternalScope;
  const [matchCounts, setMatchCounts] = useState({});

  // The unfiltered data for every layer, captured the first time each
  // layer's data shows up. Searching filters *from* this snapshot, never
  // from whatever a previous search already narrowed things down to —
  // otherwise deleting a character couldn't bring results back.
  const originalData = useRef({});
  const debounceTimer = useRef(null);

  useEffect(() => {
    Object.entries(layers).forEach(([name, cfg]) => {
      if (cfg.data && cfg.data.length > 0 && !originalData.current[name]) {
        originalData.current[name] = cfg.data;
      }
    });
  }, [layers]);

  const layerNames = Object.keys(layers);

  const runSearch = (rawQuery, activeScope) => {
    const q = rawQuery.trim().toLowerCase();
    const inScope = activeScope === ALL_LAYERS_KEY ? layerNames : [activeScope];
    const counts = {};

    setLayers((prev) => {
      const next = { ...prev };

      layerNames.forEach((name) => {
        const source = originalData.current[name] || prev[name].data;

        if (!q || !inScope.includes(name)) {
          next[name] = { ...prev[name], data: source };
          return;
        }

        const fields = getSearchFields(name, source);
        const filtered = source.filter((item) =>
          fields.some((key) => {
            const value = item[key];
            return value != null && String(value).toLowerCase().includes(q);
          })
        );

        next[name] = { ...prev[name], data: filtered };
        counts[name] = filtered.length;
      });

      return next;
    });

    setMatchCounts(q ? counts : {});
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => runSearch(value, scope), 300);
  };

  const handleScopeChange = (e) => {
    const value = e.target.value;
    setScope(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    runSearch(query, value);
  };

  const handleSearchClick = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    runSearch(query, scope);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearchClick();
    if (e.key === "Escape") handleClear();
  };

  const handleClear = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setQuery("");
    setMatchCounts({});
    setLayers((prev) => {
      const next = { ...prev };
      layerNames.forEach((name) => {
        next[name] = { ...prev[name], data: originalData.current[name] || prev[name].data };
      });
      return next;
    });
  };

  const totalMatches = useMemo(
    () => Object.values(matchCounts).reduce((sum, n) => sum + n, 0),
    [matchCounts]
  );

  return (
    <div className="search-bar">
      <div className="search-bar-row">
        <select className="search-select" value={scope} onChange={handleScopeChange}>
          <option value={ALL_LAYERS_KEY}>All layers</option>
          {layerNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        <input
          type="text"
          className="search-input"
          placeholder="Search account, walk route, serial…"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />

        {query && (
          <button type="button" className="search-clear-btn" onClick={handleClear} aria-label="Clear search">
            <FaTimes />
          </button>
        )}

        <button type="button" className="search-btn" onClick={handleSearchClick}>
          <FaSearch />
          <span className="tooltip">Search</span>
        </button>
      </div>

      {query && (
        <div className="search-results">
          <span className="search-results-total">
            {totalMatches} match{totalMatches === 1 ? "" : "es"}
          </span>
          {scope === ALL_LAYERS_KEY &&
            Object.entries(matchCounts)
              .filter(([, count]) => count > 0)
              .map(([name, count]) => (
                <span className="search-results-chip" key={name}>
                  {name}: {count}
                </span>
              ))}
        </div>
      )}
    </div>
  );
}
